// A document of the global Claude in a dialog: read it, download it as Markdown or its tables as CSV for Excel, print
// it (also to PDF), keep it at a deal. Links in it lead into the pipeline like in the chat.
import type { ClaudeDoc } from "../api";
import { markdown, tablesToCsv } from "../markdown";
import { loadDealDetail, render, S } from "../state";
import { esc, fmtDT, toast } from "../util";
import { openPipelineLink } from "./links";

let current: ClaudeDoc | null = null;

function dialog(): HTMLDialogElement {
  let d = document.querySelector<HTMLDialogElement>("dialog#doc");
  if (!d) {
    d = document.createElement("dialog");
    d.id = "doc";
    d.className = "orig doc";
    d.addEventListener("click", (e) => void onClick(e, d!));
    d.addEventListener("submit", (e) => void onSubmit(e));
    document.body.append(d);
  }
  return d;
}

const fileName = (title: string, ext: string) => `${title.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "Dokument"}.${ext}`;

function download(name: string, type: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Prints through a hidden frame (no pop-up): the browser's dialog also saves as PDF. */
function print(doc: ClaudeDoc) {
  const frame = Object.assign(document.createElement("iframe"), { title: "Druck" });
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
  document.body.append(frame);
  const w = frame.contentWindow!;
  w.document.open();
  w.document.write(`<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(doc.title)}</title><style>
    body{font:11pt/1.5 "IBM Plex Sans",system-ui,sans-serif;color:#13202B;margin:18mm}
    h1{font-size:18pt;margin:0 0 4pt}h2{font-size:14pt}h3{font-size:12pt}.meta{color:#586775;font-size:9pt;margin-bottom:14pt}
    table{border-collapse:collapse;width:100%;font-size:9.5pt}th,td{border:1px solid #C9D1DA;padding:3pt 5pt;text-align:left;vertical-align:top}th{background:#EEF1F5}
    .link{all:unset}a{color:inherit}blockquote{border-left:3px solid #C9D1DA;margin:0;padding-left:8pt;color:#41505D}code{font-family:"IBM Plex Mono",monospace;font-size:9pt}
  </style></head><body><h1>${esc(doc.title)}</h1><div class="meta">Valphira Pipeline · erstellt von Claude für ${esc(doc.by)} · ${esc(fmtDT(doc.createdAt))}${doc.dealRef ? ` · ${esc(doc.dealRef)}` : ""}</div>${markdown(doc.markdown)}</body></html>`);
  w.document.close();
  w.focus();
  w.print();
  setTimeout(() => frame.remove(), 60_000);
}

function paint(d: HTMLDialogElement, doc: ClaudeDoc) {
  const csv = tablesToCsv(doc.markdown) !== null;
  const canKeep = doc.mine && !S.readOnly;
  d.innerHTML = `<div class="orig-h"><div class="orig-meta"><b>${esc(doc.title)}</b>
      <span>Von Claude für ${esc(doc.by)} · ${esc(fmtDT(doc.createdAt))}${doc.dealRef ? ` · am Deal <button type="button" class="link" data-link="deal:${esc(doc.dealRef)}">${esc(doc.dealRef)}</button>` : ""}</span></div>
      <button class="xbtn" data-doc-close aria-label="Schließen">×</button></div>
    <div class="doc-bar">
      <button class="btn" data-doc="md">Markdown</button>
      ${csv ? `<button class="btn" data-doc="csv">Excel (CSV)</button>` : ""}
      <button class="btn" data-doc="print">Drucken / PDF</button>
      ${
        canKeep
          ? `<form class="doc-deal" data-doc-deal><input name="ref" placeholder="V-2026-…" value="${esc(doc.dealRef ?? "")}" aria-label="Vorgangsnummer"><button class="btn">${doc.dealRef ? "Ablage ändern" : "Am Deal ablegen"}</button>${doc.dealRef ? `<button class="btn" type="button" data-doc="unlink">Vom Deal lösen</button>` : ""}</form>`
          : ""
      }
    </div>
    <div class="doc-body md">${markdown(doc.markdown)}</div>`;
}

export async function openDocument(id: string): Promise<void> {
  const d = dialog();
  d.innerHTML = `<div class="orig-h"><b>Dokument wird geladen …</b><button class="xbtn" data-doc-close aria-label="Schließen">×</button></div>`;
  if (!d.open) d.showModal();
  try {
    current = await S.api.claudeDoc(id);
    paint(d, current);
  } catch {
    d.close();
    toast("Das Dokument ließ sich nicht laden.");
  }
}

async function keep(ref: string | null) {
  if (!current) return;
  try {
    const s = await S.api.claudeDocDeal(current.id, ref);
    current = { ...current, dealId: s.dealId, dealRef: s.dealRef };
    paint(dialog(), current);
    toast(s.dealRef ? `Am Deal ${s.dealRef} abgelegt.` : "Vom Deal gelöst.");
    if (S.openId) await loadDealDetail().catch(() => undefined);
    render();
  } catch (err) {
    toast(err instanceof Error ? err.message : "Das hat nicht geklappt.");
  }
}

async function onClick(e: MouseEvent, d: HTMLDialogElement) {
  const t = e.target as HTMLElement;
  if (t === d || t.closest("[data-doc-close]")) return d.close();
  const linkEl = t.closest<HTMLElement>("[data-link]");
  if (linkEl?.dataset.link) {
    d.close();
    return openPipelineLink(linkEl.dataset.link, linkEl.textContent ?? undefined);
  }
  const action = t.closest<HTMLElement>("[data-doc]")?.dataset.doc;
  if (!current || !action) return;
  if (action === "md") download(fileName(current.title, "md"), "text/markdown;charset=utf-8", current.markdown);
  if (action === "csv") download(fileName(current.title, "csv"), "text/csv;charset=utf-8", tablesToCsv(current.markdown) ?? "");
  if (action === "print") print(current);
  if (action === "unlink") await keep(null);
}

async function onSubmit(e: SubmitEvent) {
  const form = (e.target as HTMLElement).closest<HTMLFormElement>("[data-doc-deal]");
  if (!form) return;
  e.preventDefault();
  const ref = (new FormData(form).get("ref") as string).trim();
  if (ref) await keep(ref);
}
